/**
 * Diagnostic — moteur du pop-up plein écran.
 *
 * Un seul <diagnostic-modal> existe par page (rendu par
 * snippets/diagnostic-modal.liquid depuis layout/theme.liquid). Il n'est
 * couplé à aucune section : tout élément portant [data-diagnostic-open]
 * l'ouvre, et les URLs `#diagnostic` / `?diagnostic=1` aussi (campagnes,
 * liens de menu).
 *
 * Les écrans sont des éléments [data-diagnostic-step] rendus côté serveur.
 * La navigation est portée par les réponses elles-mêmes :
 *   <button data-diagnostic-answer
 *           data-question="need" data-value="bureau" data-next="a-space">
 * Ajouter un écran = ajouter du Liquid + un data-next, pas du JS.
 *
 * `trapFocus` / `removeTrapFocus` viennent de main.js, chargé globalement.
 */

/* global trapFocus, removeTrapFocus */

/** Validation d'adresse : on écarte les fautes de frappe, pas plus. */
const DIAGNOSTIC_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

if (!customElements.get('diagnostic-modal')) {
  class DiagnosticModal extends HTMLElement {
    constructor() {
      super();

      /** @type {Object<string, {value: string, tags: string[]}>} */
      this.answers = {};
      /** @type {string[]} Pile des écrans visités, pour le retour arrière. */
      this.trail = [];
      this.scrollY = 0;

      this.keyupHandler = this.keyupHandler.bind(this);
    }

    connectedCallback() {
      this.steps = Array.from(this.querySelectorAll('[data-diagnostic-step]'));
      this.firstStepId = this.steps.length ? this.steps[0].dataset.diagnosticStep : null;
      this.panel = this.querySelector('.diagnostic__panel');
      this.promo = this.querySelector('[data-diagnostic-promo]');
      this.progress = this.querySelector('[data-diagnostic-progress]');
      this.progressFill = this.querySelector('[data-diagnostic-progress-fill]');
      this.footer = this.querySelector('[data-diagnostic-footer]');
      this.nextBtn = this.querySelector('[data-diagnostic-next]');
      this.emailForm = this.querySelector('.diagnostic-email__form');
      this.emailInput = this.querySelector('[data-diagnostic-email]');
      this.emailError = this.querySelector('[data-diagnostic-email-error]');
      this.emailTags = this.querySelector('[data-diagnostic-tags]');
      this.emailReturn = this.querySelector('[data-diagnostic-return]');
      this.submitBtn = this.querySelector('[data-diagnostic-submit]');
      this.submitError = this.querySelector('[data-diagnostic-submit-error]');

      this.addEventListener('click', this.handleClick.bind(this));

      // `{% form 'customer' %}` pose une action du type `/contact#contact_form`.
      // Le navigateur reporte ce fragment sur la cible de la redirection : le
      // client arrivait sur la page de reco ancré en bas au lieu du haut. On le
      // retire ici, l'ancre ne sert à rien dans le pop-up.
      if (this.emailForm) {
        const action = this.emailForm.getAttribute('action');
        if (action && action.includes('#')) this.emailForm.setAttribute('action', action.split('#')[0]);
      }

      if (this.emailInput) this.emailInput.addEventListener('input', this.validateEmail.bind(this));
      if (this.emailForm) this.emailForm.addEventListener('submit', this.submit.bind(this));

      this.syncChrome();

      // Doit tourner avant le filet de redirection plus bas dans ce fichier :
      // il annule sessionStorage['diagnostic:redirect'] si le POST a échoué.
      if (this.submitError) this.handleServerError();
    }

    /**
     * Le POST précédent a échoué côté Shopify (captcha, adresse déjà
     * inscrite…) : la page revient ici sans compte créé. Le filet de
     * redirection ne doit pas le confondre avec un `return_to` simplement
     * pas honoré après un succès — sinon le client atterrit sur la page de
     * reco en croyant son compte créé, alors qu'il ne l'est pas.
     */
    handleServerError() {
      try {
        sessionStorage.removeItem('diagnostic:redirect');
      } catch (error) {
        // Navigation privée : rien à nettoyer.
      }

      this.showStep('email');
      this.open();
      if (this.submitBtn) this.submitBtn.disabled = false;
    }

    /**
     * Délègue les clics internes : réponse, retour, fermeture.
     * @param {MouseEvent} evt - Event object.
     */
    handleClick(evt) {
      const answer = evt.target.closest('[data-diagnostic-answer]');
      if (answer) {
        this.recordAnswer(answer);
        return;
      }

      if (evt.target.closest('[data-diagnostic-next]')) {
        this.next();
        return;
      }

      if (evt.target.closest('[data-diagnostic-back]')) {
        this.back();
        return;
      }

      if (evt.target.closest('[data-diagnostic-close]')) {
        this.close();
      }
    }

    /**
     * Enregistre une réponse et avance à l'écran suivant s'il existe.
     * @param {HTMLElement} btn - Bouton de réponse cliqué.
     */
    recordAnswer(btn) {
      const { question, value, next, flowTotal } = btn.dataset;
      if (!question) return;

      // Les parcours n'ont pas le même nombre d'écrans (3 questions pour
      // l'assise alternative, 4 pour les autres, et 3 pour la chaise si le
      // budget saute l'écran matière). L'écran e-mail tient donc son total de
      // la réponse qui l'a décidé : l'orientation, ou le palier de budget.
      if (flowTotal) this.flowTotal = parseInt(flowTotal, 10);

      this.answers[question] = { value: value || '' };

      // Marque visuellement la réponse retenue dans l'écran courant.
      const group = btn.closest('[data-diagnostic-step]');
      if (group) {
        group.querySelectorAll('[data-diagnostic-answer]').forEach((el) => {
          el.setAttribute('aria-pressed', el === btn ? 'true' : 'false');
        });
      }

      this.dispatchEvent(
        new CustomEvent('diagnostic:answer', {
          bubbles: true,
          detail: { question, answer: this.answers[question], answers: this.answers }
        })
      );

      // Écran d'orientation : le clic vaut validation, on enchaîne.
      // Écrans question : on attend « Suivant » (cf. maquette 707:2985).
      if (group && group.hasAttribute('data-auto-advance')) {
        // `next` reste vide tant que l'écran suivant n'est pas construit :
        // la réponse est mémorisée, le parcours s'arrête là sans erreur.
        if (next) this.showStep(next);
        return;
      }

      if (this.nextBtn) this.nextBtn.disabled = false;
    }

    /** Valide l'écran courant et avance vers l'écran porté par la réponse. */
    next() {
      const current = this.currentStep();
      if (!current) return;

      const selected = current.querySelector('[data-diagnostic-answer][aria-pressed="true"]');
      if (!selected || !selected.dataset.next) return;

      this.showStep(selected.dataset.next);
    }

    /** @returns {?HTMLElement} L'écran actuellement affiché. */
    currentStep() {
      return this.steps.find((step) => !step.hidden) || null;
    }

    /**
     * Aligne l'habillage (compteur, barre, pied d'écran) sur l'écran courant.
     * Un écran sans `data-index` est un écran d'orientation : ni barre ni pied.
     */
    syncChrome() {
      const current = this.currentStep();
      if (!current) return;

      const isFinal = current.hasAttribute('data-final');
      const index = isFinal ? this.flowTotal : parseInt(current.dataset.index, 10);
      const total = isFinal ? this.flowTotal : parseInt(current.dataset.total, 10);
      const counted = Number.isFinite(index) && Number.isFinite(total) && total > 0;

      if (this.progress) {
        this.progress.hidden = !counted;
        if (counted) {
          this.progress.setAttribute('aria-valuenow', String(index));
          this.progress.setAttribute('aria-valuemin', '1');
          this.progress.setAttribute('aria-valuemax', String(total));
        }
      }

      if (this.progressFill && counted) {
        this.progressFill.style.width = `${(index / total) * 100}%`;
      }

      // Sur l'écran final, le CTA du formulaire remplace Retour / Suivant.
      if (this.footer) this.footer.hidden = !counted || isFinal;

      if (this.nextBtn) {
        this.nextBtn.disabled = !current.querySelector('[data-diagnostic-answer][aria-pressed="true"]');
      }

      if (isFinal) {
        this.validateEmail();
        if (this.emailInput) this.emailInput.focus();
        // Lancé sans attendre : les requêtes tournent pendant que le client
        // saisit son adresse, donc la redirection ne paie presque rien.
        this.catalogReady = this.refreshCatalog();
      }
    }

    /**
     * Réponses à plat, sous la forme attendue par le moteur.
     * @returns {Object<string, string>}
     */
    plainAnswers() {
      const flat = {};
      Object.keys(this.answers).forEach((question) => {
        flat[question] = this.answers[question].value;
      });
      return flat;
    }

    /**
     * Remplace prix et disponibilité de référence par ceux de la boutique,
     * pour le produit retenu et ses replis seulement — une poignée de
     * requêtes, pas les 27 du catalogue.
     * En cas d'échec on garde les valeurs de la spec : une reco légèrement
     * datée vaut mieux qu'une absence de reco.
     */
    async refreshCatalog() {
      const engine = window.DiagnosticEngine;
      if (!engine) return;

      const keys = engine.candidateKeys(this.plainAnswers());

      await Promise.all(
        keys.map(async (key) => {
          try {
            const response = await fetch(`/products/${engine.CATALOG[key].handle}.js`);
            if (response.ok) engine.applyLive(key, await response.json());
          } catch (error) {
            // Produit laissé à sa valeur de référence.
          }
        })
      );
    }

    /** Active le CTA dès que l'adresse saisie est plausible. */
    validateEmail() {
      if (!this.emailInput || !this.submitBtn) return;

      const valid = DIAGNOSTIC_EMAIL_RE.test(this.emailInput.value.trim());
      this.submitBtn.disabled = !valid;

      // L'erreur n'apparaît qu'après une tentative d'envoi, pas pendant la
      // frappe — sinon elle s'affiche dès le premier caractère.
      if (valid && this.emailError) this.emailError.hidden = true;
    }

    /**
     * Inscrit l'adresse puis part vers la page de recommandation.
     *
     * L'envoi est natif : posté en `fetch`, Shopify crée bien le client mais
     * n'applique pas `contact[tags]`. C'est donc `return_to` qui porte la
     * redirection, renseigné juste avant l'envoi.
     *
     * L'événement est intercepté le temps de calculer la reco (les prix live
     * peuvent encore être en vol), puis l'envoi est relancé à la main.
     *
     * L'e-mail n'est volontairement pas mis dans l'URL.
     *
     * @param {SubmitEvent} [evt] - Event object.
     */
    async submit(evt) {
      if (evt) evt.preventDefault();
      if (!this.emailInput) return;

      const email = this.emailInput.value.trim();

      if (!DIAGNOSTIC_EMAIL_RE.test(email)) {
        if (this.emailError) this.emailError.hidden = false;
        this.emailInput.focus();
        return;
      }

      if (this.emailError) this.emailError.hidden = true;
      if (this.submitBtn) this.submitBtn.disabled = true;

      // Les prix et disponibilités live sont peut-être encore en vol.
      if (this.catalogReady) await this.catalogReady;

      const reco = this.recommendation();

      if (this.emailTags) {
        // Les réponses ne sont volontairement pas taguées : elles partent
        // dans l'URL de la page de reco, et n'ont pas à encombrer la fiche
        // client. Seuls le déclencheur de remise et la reco restent.
        const all = this.emailTags.value.split(',').map((t) => t.trim()).filter(Boolean);

        if (reco) {
          // Un Flow purge les tags `diagnostic-*` une fois la remise
          // consommée, `diagnostic-effectue-ok` en tête. La trace du passage
          // vit donc sous le préfixe `reco-`, hors de portée de la purge —
          // c'est le nom du tag qui la protège, pas une exception dans le
          // Flow. Reco et date tiennent dans le même tag : le couple reste
          // lisible après plusieurs diagnostics, quitte à le découper à
          // l'export.
          all.push(reco.slug, `${reco.slug.replace(/^diagnostic-/, 'reco-')}-${this.shopDateStamp()}`);
        }

        this.emailTags.value = [...new Set(all)].join(',');
      }

      const target = this.recoUrl(reco);
      if (this.emailReturn) this.emailReturn.value = target;

      // Filet : si la boutique n'honore pas `return_to`, Shopify renvoie sur
      // la page d'origine. On rattrape la redirection au chargement suivant.
      try {
        sessionStorage.setItem('diagnostic:redirect', target);
      } catch (error) {
        // Navigation privée : on se passe du filet.
      }

      this.dispatchEvent(
        new CustomEvent('diagnostic:submit', {
          bubbles: true,
          detail: {
            email,
            answers: this.answers,
            reco,
            tags: this.emailTags ? this.emailTags.value : ''
          }
        })
      );

      // `form.submit()` et non `requestSubmit()` : on ne veut pas relancer le
      // gestionnaire qu'on vient d'intercepter.
      if (this.emailForm) this.emailForm.submit();
      else window.location.assign(target);
    }

    /**
     * Date du jour au fuseau de la boutique, au format AAAAMMDD.
     *
     * Le décalage vient de Liquid, pas du navigateur : un visiteur à Tokyo
     * serait sinon archivé au lendemain, et la date du tag ne collerait plus
     * à celle des commandes. On décale l'horodatage puis on lit les
     * composantes UTC, ce qui donne la date telle que la boutique la voit.
     *
     * @returns {string}
     */
    shopDateStamp() {
      const offset = /^([+-])(\d{2})(\d{2})$/.exec((this.dataset.shopUtcOffset || '').trim());
      const pad = (n) => String(n).padStart(2, '0');

      if (!offset) {
        const local = new Date();
        return `${local.getFullYear()}${pad(local.getMonth() + 1)}${pad(local.getDate())}`;
      }

      const minutes = (Number(offset[2]) * 60 + Number(offset[3])) * (offset[1] === '-' ? -1 : 1);
      const shop = new Date(Date.now() + minutes * 60000);

      return `${shop.getUTCFullYear()}${pad(shop.getUTCMonth() + 1)}${pad(shop.getUTCDate())}`;
    }

    /**
     * Produit retenu pour ce parcours, disponibilité live comprise.
     * @returns {?{keys: string[], slug: string, products: object[]}}
     */
    recommendation() {
      const engine = window.DiagnosticEngine;
      if (!engine || !this.answers.need) return null;

      try {
        return engine.recommend(this.plainAnswers());
      } catch (error) {
        // Un parcours incomplet ne doit pas bloquer la redirection.
        return null;
      }
    }

    /**
     * Construit l'URL de la page de recommandation.
     * `reco` porte la même valeur que le tag client
     * (`diagnostic-duo-space-terrana`), pour que la page et le reciblage
     * parlent exactement la même langue. Les réponses suivent en paramètres :
     * la page en a besoin pour justifier la reco.
     * @param {?object} [reco] - Résultat de `recommendation()`.
     * @returns {string}
     */
    recoUrl(reco) {
      const base = this.dataset.recoUrl || '/pages/ma-recommandation';
      const params = new URLSearchParams();

      if (reco) params.set('reco', reco.slug);

      Object.keys(this.answers).forEach((question) => {
        params.set(question, this.answers[question].value);
      });

      const query = params.toString();
      return query ? `${base}?${query}` : base;
    }

    /**
     * Affiche un écran et masque les autres.
     * @param {string} id - Valeur de data-diagnostic-step.
     */
    showStep(id) {
      const target = this.steps.find((step) => step.dataset.diagnosticStep === id);
      if (!target) return;

      const current = this.steps.find((step) => !step.hidden);
      if (current && current !== target) this.trail.push(current.dataset.diagnosticStep);

      this.steps.forEach((step) => {
        step.hidden = step !== target;
      });

      this.syncChrome();
      if (this.panel) this.panel.scrollTop = 0;

      this.dispatchEvent(
        new CustomEvent('diagnostic:step', { bubbles: true, detail: { step: id } })
      );
    }

    /** Revient à l'écran précédent. */
    back() {
      const previous = this.trail.pop();
      if (!previous) return;

      const target = this.steps.find((step) => step.dataset.diagnosticStep === previous);
      if (!target) return;

      this.steps.forEach((step) => {
        step.hidden = step !== target;
      });

      this.syncChrome();
      if (this.panel) this.panel.scrollTop = 0;
    }

    /** Remet le parcours au premier écran. */
    reset() {
      this.answers = {};
      this.trail = [];

      this.querySelectorAll('[data-diagnostic-answer][aria-pressed]').forEach((el) => {
        el.setAttribute('aria-pressed', 'false');
      });

      this.steps.forEach((step, index) => {
        step.hidden = index !== 0;
      });

      this.syncChrome();
    }

    /**
     * Ouvre le pop-up.
     * @param {Element} [opener] - Élément déclencheur, refocalisé à la fermeture.
     */
    open(opener) {
      if (this.hasAttribute('open')) return;

      this.loadVisuals();

      // Fige la page derrière le pop-up (même mécanique que modal.js).
      this.scrollY = window.scrollY;
      document.body.classList.add('fixed');
      document.body.style.top = `-${this.scrollY}px`;

      this.setAttribute('open', '');
      this.openedBy = opener || null;

      if (typeof trapFocus === 'function') trapFocus(this);
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();

      document.addEventListener('keyup', this.keyupHandler);

      this.dispatchEvent(new CustomEvent('diagnostic:open', { bubbles: true }));
    }

    /** Ferme le pop-up et restaure la position de scroll. */
    close() {
      if (!this.hasAttribute('open')) return;

      document.body.style.top = '';
      document.body.classList.remove('fixed');
      window.scrollTo(0, this.scrollY);

      this.removeAttribute('open');

      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.openedBy);

      document.removeEventListener('keyup', this.keyupHandler);

      this.dispatchEvent(
        new CustomEvent('diagnostic:close', { bubbles: true, detail: { answers: this.answers } })
      );
    }

    /**
     * Pose les sources des visuels à la première ouverture. Le pop-up est
     * présent sur toutes les pages : sans ça, chaque page paierait le
     * téléchargement de la photo, même sans jamais ouvrir le diagnostic.
     * Les <source> sont renseignés avant les <img>, sinon <picture> a déjà
     * arbitré sur un srcset vide.
     */
    loadVisuals() {
      this.querySelectorAll('source[data-srcset]').forEach((el) => {
        el.srcset = el.dataset.srcset;
        el.removeAttribute('data-srcset');
      });

      this.querySelectorAll('img[data-src]').forEach((el) => {
        el.src = el.dataset.src;
        el.removeAttribute('data-src');
      });
    }

    /**
     * Ferme au clavier.
     * @param {KeyboardEvent} evt - Event object.
     */
    keyupHandler(evt) {
      if (evt.key === 'Escape') this.close();
    }
  }

  customElements.define('diagnostic-modal', DiagnosticModal);
}

/**
 * Point d'entrée unique : n'importe quel [data-diagnostic-open] de la page
 * ouvre le pop-up, quelle que soit la section qui l'a rendu.
 */
document.addEventListener('click', (evt) => {
  const opener = evt.target.closest('[data-diagnostic-open]');
  if (!opener) return;

  const modal = document.querySelector('diagnostic-modal');
  if (!modal) return;

  evt.preventDefault();
  modal.open(opener);
});

/**
 * Rattrapage de redirection. Le formulaire client part en envoi natif, seul
 * moyen de faire appliquer `contact[tags]` ; si la boutique n'honore pas
 * `return_to`, Shopify renvoie sur la page d'origine et le client n'aurait
 * jamais sa recommandation. On termine alors le voyage ici.
 *
 * Le compte n'est pas forcément créé à la première page qui suit l'envoi :
 * Shopify peut intercaler un écran de vérification (captcha) avant de
 * revenir. `data-customer-logged-in` (posé par diagnostic-modal.liquid) dit
 * si le client est réellement connecté sur la page où on atterrit — sans ce
 * garde, on foncerait vers la reco dès que l'URL diffère de la cible, y
 * compris en plein milieu d'un captcha non résolu, laissant croire à un
 * compte créé qui ne l'est pas.
 */
(() => {
  let target = null;

  try {
    target = sessionStorage.getItem('diagnostic:redirect');
  } catch (error) {
    return;
  }

  if (!target) return;

  const modal = document.querySelector('diagnostic-modal');
  const accountCreated = modal && modal.dataset.customerLoggedIn === 'true';

  // Compte pas encore créé : on laisse la clé en place, elle doit survivre
  // aux pages intermédiaires jusqu'à l'arrivée réelle.
  if (!accountCreated) return;

  try {
    sessionStorage.removeItem('diagnostic:redirect');
  } catch (error) {
    // Navigation privée : sans incidence, la clé ne survit pas au thread.
  }

  // `return_to` a fonctionné : on est déjà arrivé, rien à faire.
  if (new URL(target, window.location.origin).pathname === window.location.pathname) return;

  window.location.replace(target);
})();

/** Ouverture par URL : /?diagnostic=1 ou /#diagnostic (emails, ads, menu). */
(() => {
  const autoOpen = () => {
    const wanted =
      window.location.hash === '#diagnostic' ||
      new URLSearchParams(window.location.search).get('diagnostic') === '1';
    if (!wanted) return;

    const modal = document.querySelector('diagnostic-modal');
    if (modal) modal.open();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoOpen);
  } else {
    autoOpen();
  }
})();
